/** PROTOTYPE — HRP reconciliation UI exploration. Route: /finance/billing-rate-builder?prototype=reconciliation&variant=A */

import { useCallback, useState } from "react";
import { useSearchParams } from "react-router";
import Stepper from "../../shared/Stepper";
import {
    MOCK_HRP_SNAPSHOT,
    VARIANT_META,
    createMockReconciliationRows,
} from "./hrpReconciliationMockData";
import {
    PrototypeStatePanel,
    applyCreateToRow,
    applyIgnoreToRow,
    applyKeepToRow,
    applyUpdateToRow,
} from "./hrpReconciliationPrototypeShared";
import HrpReconciliationVariantA from "./HrpReconciliationVariantA";
import HrpReconciliationVariantB from "./HrpReconciliationVariantB";
import HrpReconciliationVariantC from "./HrpReconciliationVariantC";
import PrototypeVariantSwitcher from "./PrototypeVariantSwitcher";

const STEPS = [
    { label: "Import Data" },
    { label: "Components" },
    { label: "Rates" },
    { label: "Review & Submit" },
];

const HrpReconciliationPrototype = () => {
    const [searchParams] = useSearchParams();
    const variant = searchParams.get("variant") ?? "A";
    const [rows, setRows] = useState(createMockReconciliationRows);

    const updateRow = useCallback((rowId, transform) => {
        setRows((prev) => prev.map((row) => (row.rowId === rowId ? transform(row) : row)));
    }, []);

    const onUpdate = useCallback((rowId) => updateRow(rowId, applyUpdateToRow), [updateRow]);
    const onKeep = useCallback((rowId) => updateRow(rowId, applyKeepToRow), [updateRow]);
    const onIgnore = useCallback((rowId) => updateRow(rowId, applyIgnoreToRow), [updateRow]);
    const onCreate = useCallback((rowId) => updateRow(rowId, applyCreateToRow), [updateRow]);

    const reset = () => setRows(createMockReconciliationRows());

    const variantProps = {
        rows,
        hrpSnapshot: MOCK_HRP_SNAPSHOT,
        onUpdate,
        onKeep,
        onIgnore,
        onCreate,
    };

    const meta = VARIANT_META[variant] ?? VARIANT_META.A;

    return (
        <>
            <div className="billing-rate-builder__prototype-banner">
                <strong>PROTOTYPE</strong>
                {" — "}
                HRP reconciliation UI ({meta.label}). Mock Wheeler data only; no HRP calls.
                {" "}
                <button type="button" className="btn" onClick={reset}>Reset rows</button>
            </div>

            <div className="billing-rate-builder__header">
                <div className="title">Billing Rate Builder</div>
            </div>

            <div className="billing-rate-builder__stepper-wrap steps-wrapper">
                <Stepper steps={STEPS} activeStep={2} onClickHandler={() => {}} />
            </div>

            <div className="billing-rate-builder__form form scrollable-form">
                <p className="billing-rate-builder__prototype-variant-desc">{meta.description}</p>

                {variant === "B" && <HrpReconciliationVariantB {...variantProps} />}
                {variant === "C" && <HrpReconciliationVariantC {...variantProps} />}
                {(variant === "A" || !["B", "C"].includes(variant)) && (
                    <HrpReconciliationVariantA {...variantProps} />
                )}

                <PrototypeStatePanel rows={rows} />
            </div>

            <PrototypeVariantSwitcher />
        </>
    );
};

export default HrpReconciliationPrototype;
